
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  DollarSign, 
  ArrowDown, 
  ArrowRight, 
  FileText, 
  Download
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDoctorPayments } from './hooks/useDoctorPayments';
import { WithdrawalRequestModal } from './components/WithdrawalRequestModal';
import { DateRange } from 'react-day-picker';
import { useToast } from '@/components/ui/use-toast';

const FinancePage = () => {
  // Define all hooks at the top level
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isWithdrawalModalOpen, setIsWithdrawalModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    to: new Date(),
  });
  
  // Use useDoctorPayments hook
  const { payments, summary, isLoading } = useDoctorPayments({ 
    status: statusFilter !== 'all' ? statusFilter : undefined,
    dateFrom: dateRange.from,
    dateTo: dateRange.to
  });

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'transferred':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const handleViewPaymentDetails = (paymentId: string) => {
    navigate(`/doctor-area/payments/${paymentId}`);
    
    toast({
      title: "Payment details",
      description: `Viewing details for payment #${paymentId}`,
    });
  };

  const handleViewInvoice = (invoiceUrl: string, paymentId: string) => {
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank');
    } else {
      toast({
        title: "Invoice not available",
        description: `The invoice for payment #${paymentId} is not available yet.`,
        variant: "destructive"
      });
    }
  };

  const handleViewPatientProfile = (patientId: number | string, patientName: string) => {
    // Convert patientId to number if it's a string
    const numericPatientId = typeof patientId === 'string' ? parseInt(patientId, 10) : patientId;
    
    navigate(`/doctor-area/case-file/${numericPatientId}`);
    
    toast({
      title: "Patient profile",
      description: `Viewing profile for ${patientName}`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">My Payments</h1>
          <p className="text-muted-foreground">Manage and track your earnings</p>
        </div>
        <Button onClick={() => setIsWithdrawalModalOpen(true)}>
          <ArrowDown className="mr-2 h-4 w-4" />
          Request Withdrawal
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Earnings</CardDescription>
            <CardTitle className="text-2xl font-bold flex items-center">
              <DollarSign className="h-5 w-5 text-muted-foreground mr-1" />
              ${summary.totalEarnings.toFixed(2)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Withdrawable Balance</CardDescription>
            <CardTitle className="text-2xl font-bold flex items-center">
              <DollarSign className="h-5 w-5 text-muted-foreground mr-1" />
              ${summary.withdrawableBalance.toFixed(2)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Pending Transfers</CardDescription>
            <CardTitle className="text-2xl font-bold flex items-center">
              <DollarSign className="h-5 w-5 text-muted-foreground mr-1" />
              ${summary.pendingTransfers.toFixed(2)}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Payments</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <DatePickerWithRange 
                date={dateRange}
                onDateChange={setDateRange}
              />
            </div>
            <div className="w-full md:w-[200px]">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Payment Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="transferred">Transferred</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table/Cards */}
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>
            {payments.length} payments found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <p>Loading payment data...</p>
            </div>
          ) : payments.length > 0 ? (
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id} className={payment.status === 'pending' ? 'bg-muted/50' : ''}>
                      <TableCell>
                        <button 
                          className="hover:underline text-left font-medium"
                          onClick={() => handleViewPatientProfile(payment.patientId, payment.patientName)}
                        >
                          {payment.patientName}
                        </button>
                      </TableCell>
                      <TableCell>{payment.service}</TableCell>
                      <TableCell className="font-medium">${payment.amount.toFixed(2)}</TableCell>
                      <TableCell>{new Date(payment.date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(payment.status)}>
                          {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleViewInvoice(payment.invoiceUrl, payment.id)}
                            title="View Invoice"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleViewPaymentDetails(payment.id)}
                            title="View Details"
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No payments found matching your filters.</p>
              <Button variant="outline" onClick={() => {
                setStatusFilter('all');
                setDateRange({
                  from: new Date(new Date().setMonth(new Date().getMonth() - 3)),
                  to: new Date(),
                });
              }}>Reset Filters</Button>
            </div>
          )}

          {/* Mobile Payment Cards */}
          {!isLoading && payments.length > 0 && (
            <div className="md:hidden space-y-4">
              {payments.map((payment) => (
                <Card key={payment.id} className={payment.status === 'pending' ? 'bg-muted/50' : ''}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <button 
                          className="font-medium hover:underline text-left"
                          onClick={() => handleViewPatientProfile(payment.patientId, payment.patientName)}
                        >
                          {payment.patientName}
                        </button>
                        <p className="text-sm text-muted-foreground">{payment.service}</p>
                      </div>
                      <Badge variant={getStatusBadgeVariant(payment.status)}>
                        {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center mt-4">
                      <div>
                        <p className="text-lg font-bold">${payment.amount.toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground">{new Date(payment.date).toLocaleDateString()}</p>
                      </div>
                      <div className="flex">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleViewInvoice(payment.invoiceUrl, payment.id)}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleViewPaymentDetails(payment.id)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <WithdrawalRequestModal 
        open={isWithdrawalModalOpen} 
        onClose={() => setIsWithdrawalModalOpen(false)}
        availableBalance={summary.withdrawableBalance}
      />
    </div>
  );
};

export default FinancePage;
